import React, { useEffect } from 'react';

// Utils
import { getCurrentMoment, formatDate } from 'Shared/utils/DateTime.utils';

// Hooks
import { useUpdateAiSetting } from 'Shared/utils/hooks/use-update-ai-setting';

// Types
import { UpdateAiTemplateTypeType } from 'Shared/types/UpdateAiTemplate.type';

// Components
import WpSelectV2, { StrictOption } from 'Wpds/components/select/WpSelectV2';
import InputSources from './partials/InputSources';
import InstructionsTitle from './partials/InstructionsTitle';

// Styles
import './SettingsRail.scss';

enum DATE_OPTIONS {
  this_week = 'this_week',
  last_week = 'last_week',
  this_month = 'this_month',
  last_month = 'last_month',
}

interface DateOptionType extends StrictOption {
  start: string;
  end: string;
}

type Props = {
  updateAiSettingSlug: string;
  handleUpdateDates: Function;
};

const SettingsRail = ({ handleUpdateDates, updateAiSettingSlug }: Props) => {
  const { template } = useUpdateAiSetting({ slug: updateAiSettingSlug });
  const salesTemplate = template.templateType === UpdateAiTemplateTypeType.SALES;

  const dateOptions: DateOptionType[] = [
    {
      value: DATE_OPTIONS.this_week,
      label: `This week (${formatDate(getCurrentMoment().startOf('week'), 'M/D')} - today)`,
      start: formatDate(getCurrentMoment().startOf('week'), 'YYYY-MM-DD'),
      end: formatDate(getCurrentMoment(), 'YYYY-MM-DD'),
    },
    {
      value: DATE_OPTIONS.last_week,
      label: `Last week (${formatDate(getCurrentMoment().subtract(7, 'days').startOf('week'), 'M/D')} - ${formatDate(
        getCurrentMoment().subtract(7, 'days').endOf('week'),
        'M/D'
      )})`,
      start: formatDate(getCurrentMoment().subtract(7, 'days').startOf('week'), 'YYYY-MM-DD'),
      end: formatDate(getCurrentMoment().subtract(7, 'days').endOf('week'), 'YYYY-MM-DD'),
    },
    {
      value: DATE_OPTIONS.this_month,
      label: `This month (${formatDate(getCurrentMoment().startOf('month'), 'M/D')} - today)`,
      start: formatDate(getCurrentMoment().startOf('month'), 'YYYY-MM-DD'),
      end: formatDate(getCurrentMoment(), 'YYYY-MM-DD'),
    },
    {
      value: DATE_OPTIONS.last_month,
      label: `Last month (${formatDate(getCurrentMoment().subtract(1, 'month').startOf('month'), 'M/D')} - ${formatDate(
        getCurrentMoment().subtract(1, 'month').endOf('month'),
        'M/D'
      )})`,
      start: formatDate(getCurrentMoment().subtract(1, 'month').startOf('month'), 'YYYY-MM-DD'),
      end: formatDate(getCurrentMoment().subtract(1, 'month').endOf('month'), 'YYYY-MM-DD'),
    },
  ];

  const handleSetDates = (option: DATE_OPTIONS) => {
    const selection: DateOptionType = dateOptions.find(i => i.value === option);

    handleUpdateDates({
      end: selection.end,
      start: selection.start,
    });
  };

  useEffect(() => {
    handleSetDates(DATE_OPTIONS.this_week);
  }, []);

  const baseClassName = 'SettingsRail';

  return (
    <div className={baseClassName}>
      <InstructionsTitle
        title="Inputs"
        description="A status update is only as good as what you put into it. Make sure work sources are added below."
      />
      {!salesTemplate && (
        <div className={`${baseClassName}__section`}>
          <WpSelectV2
            id="date"
            label="Date Range"
            options={dateOptions}
            onChange={handleSetDates}
            placeholder="Select date range"
            value={DATE_OPTIONS.this_week}
          />
        </div>
      )}
      <div className={`${baseClassName}__section`}>
        <InstructionsTitle
          title="Connect Integrations"
          titleTag="p"
          description="Choose at least one source to use for this report."
        />
        <InputSources />
      </div>
    </div>
  );
};

export default SettingsRail;
